<script setup lang="ts">
import type { DialogSize } from './types'

type Props = {
  modelValue: boolean
  maxWidth?: number | null
  padding?: string | null
  size?: DialogSize
  class?: string
  persistent?: boolean
  fixed?: boolean
  autofocus?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  maxWidth: 620,
  padding: null,
  size: 'sm',
  class: '',
  autofocus: true,
})

const emit = defineEmits(['update:modelValue'])

const dialogContainerElement = ref<HTMLDivElement | null>(null)

const componentClasses = computed(() => {
  const classes = ['app-dialog', `app-dialog--size-${props.size}`]

  if (props.class) classes.push(props.class)

  if (props.fixed) classes.push('app-dialog--fixed')

  return classes
})

const componentStyles = computed(() => {
  return props.maxWidth ? { maxWidth: `${props.maxWidth}px` } : {}
})

const contentStyles = computed(() => {
  return props.padding ? { padding: `${props.padding}px` } : {}
})

const onClose = () => {
  if (props.persistent) return

  emit('update:modelValue', false)
}

const onEscapeKeyUp = (event: KeyboardEvent) => {
  if (event?.key !== 'Escape') return

  onClose()
}

const setActiveState = () => {
  scrollLock()

  if (!props.autofocus) return

  setTimeout(() => {
    dialogContainerElement.value?.focus()
  }, 300)
}

watch(
  () => props.modelValue,
  (isActive) => {
    if (isActive) return setActiveState()

    scrollUnlock()
  },
)

onMounted(() => {
  window.addEventListener('keyup', onEscapeKeyUp)
})

onBeforeUnmount(() => {
  window.removeEventListener('keyup', onEscapeKeyUp)

  if (props.modelValue) scrollUnlock()
})
</script>

<template>
  <Teleport to="body">
    <transition name="ui-fade">
      <div
        v-if="modelValue"
        class="app-dialog-overlay ui-dialog-overlay"
        role="dialog"
        aria-modal="true"
      >
        <div
          ref="dialogContainerElement"
          class="app-dialog-overlay__container"
          tabindex="0"
          @mousedown.self="onClose"
        >
          <div :class="componentClasses" :style="componentStyles">
            <the-button
              v-if="!persistent"
              variant="transparent"
              icon="close-bigger"
              size="sm"
              radius="lg"
              class="app-dialog__close"
              aria-label="Закрыть диалоговое окно"
              @click="onClose"
            />

            <div class="app-dialog__content" :style="contentStyles">
              <slot />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style lang="scss" scoped>
@import '../../assets/ui-layer/styles/mixins';

.app-dialog-overlay {
  &__container {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 20px 0;
    overflow: auto;
    @include invisible-scroll-bar();
    outline: none;

    @include media('>=tablet') {
      padding: 20px;
    }
  }
}

.app-dialog {
  position: relative;
  width: calc(100% - 25px);
  margin: auto;
  background-color: var(--ui-white);
  border-radius: var(--dialog-radius);
  outline: none;

  @include media('>=desktop-mini') {
    width: 100%;
  }

  &__close {
    position: absolute !important;
    top: 8px;
    right: 8px;
    z-index: 10;
    color: var(--ui-text-primary);

    @include media('>=desktop-mini') {
      top: 12px;
      right: 12px;
    }
  }

  &__content {
    padding: var(--dialog-padding);
  }

  &--size-sm {
    --dialog-radius: 16px;
    --dialog-padding: 32px 24px 24px;

    @include media('>=desktop-mini') {
      --dialog-radius: 10px;
      --dialog-padding: 52px 30px 32px;
    }
  }

  &--size-md {
    --dialog-radius: 10px;
    --dialog-padding: 56px 16px 40px;

    @include media('>=desktop-mini') {
      --dialog-padding: 52px 30px 40px;
    }
  }

  &--fixed {
    height: 100%;
    overflow: hidden;

    .app-dialog__content {
      height: 100%;
      overflow-y: auto;
    }
  }
}
</style>
